import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface VariantSize {
  label: string;
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
}

export interface VariantColor {
  name: string;
  hex: string;
  images?: string[];
  slug?: string;
}

export interface ProductVariant {
  id: string; // computed stable id (size__color, etc.)
  size: string;
  color: string;
  colorHex?: string;
  priceModifier?: number;
  stock?: number;
  sku?: string;
  isActive?: boolean;
  images?: string[];
}

// Note: Keep existing fields for backward-compat. Add optional variant fields.
export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  images: string[];
  description: string;
  features: string[];
  category: string;
  subcategory?: string;
  rating: number;
  reviews: number;
  stock: number;
  tags: string[];
  dimensions?: {
    length: number;
    width: number;
    height: number;
  } | any; // Allow any for compatibility with services that return string dimensions
  material?: string;
  warranty?: string;
  isFeatured: boolean;
  isNewArrival: boolean;
  isBestSeller: boolean;
  createdAt: string;
  commentCount?: number;
  deliverablePincodes?: string[];

  // Optional variant support
  variantSizes?: VariantSize[];
  variantColors?: VariantColor[];
  variants?: ProductVariant[];
}

interface ProductsState {
  items: Product[];
  filteredItems: Product[];
  categories: string[];
  isLoading: boolean;
  error: string | null;
  filters: {
    category: string;
    priceRange: [number, number];
    rating: number;
    sortBy: string;
  };
}

const initialState: ProductsState = {
  items: [],
  filteredItems: [],
  categories: [],
  isLoading: false,
  error: null,
  filters: {
    category: '',
    priceRange: [0, 100000],
    rating: 0,
    sortBy: 'name',
  },
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.items = action.payload;
      state.filteredItems = action.payload;
      state.categories = [...new Set(action.payload.map(product => product.category))];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<ProductsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };

      let filtered = [...state.items];

      // Fix: Use categories array for filtering
      if (state.filters.category) {
        filtered = filtered.filter(product =>
          Array.isArray((product as any).categories)
            ? (product as any).categories.includes(state.filters.category)
            : product.category === state.filters.category
        );
      }

      filtered = filtered.filter(
        product => product.price >= state.filters.priceRange[0] &&
                  product.price <= state.filters.priceRange[1]
      );

      if (state.filters.rating > 0) {
        filtered = filtered.filter(product => product.rating >= state.filters.rating);
      }

      // Apply sorting
      switch (state.filters.sortBy) {
        case 'price-low':
          filtered.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filtered.sort((a, b) => b.price - a.price);
          break;
        case 'rating':
          filtered.sort((a, b) => b.rating - a.rating);
          break;
        case 'newest':
          filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        default:
          filtered.sort((a, b) => a.name.localeCompare(b.name));
      }

      state.filteredItems = filtered;
    },
    clearFilters: (state) => {
      state.filters = {
        category: '',
        priceRange: [0, 100000],
        rating: 0,
        sortBy: 'name',
      };
      state.filteredItems = state.items;
    },
  },
});

export const { setProducts, setLoading, setError, setFilters, clearFilters } = productsSlice.actions;
export default productsSlice.reducer;